import { useNavigate } from "react-router-dom";
import { Icon } from "../Icon";
import { InfoTip } from "../InfoTip";

/**
 * FR-DASH-01 — the KPI header bar.
 *
 * Two rules from `executive-dashboard-design` are structural here rather than
 * cosmetic: every KPI carries a period-over-period trend, and every KPI is one
 * click from the evidence behind it. A number a CCO can't interrogate is a
 * number they stop believing the first time it surprises them.
 */

export interface Kpi {
  value: number | null;
  previous: number | null;
  delta: number | null;
  direction: "improved" | "worsened" | "flat" | "unknown";
}

export interface KpiPayload {
  hasData: boolean;
  samples: number;
  priorSamples: number;
  severity: "aligned" | "erosion" | "critical" | null;
  window: { days: number };
  kpis: {
    narrativeHealthScore: Kpi;
    mptr: Kpi;
    ndi: Kpi;
    activeRisks: Kpi;
  };
}

const TREND_ICON: Record<Kpi["direction"], "arrowUp" | "arrowDown" | "minus"> =
  {
    improved: "arrowUp",
    worsened: "arrowDown",
    flat: "minus",
    unknown: "minus",
  };

export function KpiBar({
  data,
  to,
}: {
  data: KpiPayload | null;
  to: (key: string) => string;
}) {
  const navigate = useNavigate();

  if (!data) {
    return (
      <div className="kpibar">
        {[0, 1, 2, 3].map((i) => (
          <div className="kpi" key={i} aria-hidden="true">
            <div className="skeleton" style={{ height: 18, width: "55%" }} />
            <div className="skeleton" style={{ height: 44, width: "40%" }} />
            <div className="skeleton" style={{ height: 14, width: "35%" }} />
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      key: "health",
      label: "Narrative Health Score",
      kpi: data.kpis.narrativeHealthScore,
      unit: "",
      // Each explanation says what the number measures, which way is good, and
      // where it comes from — the three things someone about to put it in a
      // board deck needs and can't infer from the digits.
      hint: (
        <>
          <strong>0–100, higher is better.</strong> The inverse of the Narrative
          Drift Index: how closely the AI engines&rsquo; answers match your
          approved Ground Truth, averaged across every scored response in this
          period.
        </>
      ),
    },
    {
      key: "mptr",
      label: "Message Pull-Through",
      kpi: data.kpis.mptr,
      unit: "%",
      hint: (
        <>
          <strong>0–100%, higher is better.</strong> Of the key messages a given
          question
          <em> should</em> have surfaced, the share the engine actually carried.
          The denominator is the claims relevant to that prompt — not your whole
          pillar, which no single answer could restate.
        </>
      ),
    },
    {
      key: "ndi",
      label: "Narrative Drift Index",
      kpi: data.kpis.ndi,
      unit: "",
      hint: (
        <>
          <strong>0–100, lower is better.</strong> How far the engines have
          diverged from what you officially said, combining message
          pull-through, contradictions, semantic distance, and a judge&rsquo;s
          read on framing. 0–15 Aligned · 16–40 Erosion · 41+ Critical Drift.
        </>
      ),
    },
    {
      key: "risks",
      label: "Active Reputational Risks",
      kpi: data.kpis.activeRisks,
      unit: "",
      hint: (
        <>
          <strong>Lower is better.</strong> Unresolved findings in the Critical
          Drift band — a contradiction or omission serious enough to need an
          intervention, not just watching.
        </>
      ),
    },
  ];

  return (
    <div className="kpibar">
      {cards.map((card, index) => (
        // A div, not a button. The card is clickable AND carries an info
        // tooltip, and a <button> inside a <button> is invalid HTML that
        // browsers resolve inconsistently — so the card's click target is a
        // stretched button behind the content, and the tooltip trigger sits
        // above it. Both stay independently focusable.
        <div className="kpi" key={card.key}>
          <button
            type="button"
            className="kpi__hit"
            aria-label={`${card.label}: see the findings behind this number`}
            onClick={() => navigate(to(card.key))}
          />
          <span className="kpi__label">
            {card.label}
            <InfoTip
              label={card.label}
              align={index === cards.length - 1 ? "end" : "center"}
            >
              {card.hint}
            </InfoTip>
            <Icon
              name="arrowRight"
              aria-hidden="true"
              style={{ width: 12, height: 12 }}
            />
          </span>
          {card.kpi.value === null ? (
            // Never a zero. A brand-new Brand showing "Health 100" or "NDI 0"
            // would be a fabricated all-clear, which is worse than no number.
            <span className="kpi__value kpi__value--empty">
              Not yet measured
            </span>
          ) : (
            <span className="kpi__value">
              {card.kpi.value}
              {card.unit ? (
                <span className="kpi__unit">{card.unit}</span>
              ) : null}
            </span>
          )}
          <Trend kpi={card.kpi} days={data.window.days} />
        </div>
      ))}
    </div>
  );
}

function Trend({ kpi, days }: { kpi: Kpi; days: number }) {
  if (kpi.direction === "unknown" || kpi.delta === null) {
    return (
      <span className="kpi__trend kpi__trend--unknown">
        No prior {days}-day window to compare
      </span>
    );
  }
  const sign = kpi.delta > 0 ? "+" : "";
  return (
    <span className={`kpi__trend kpi__trend--${kpi.direction}`}>
      <Icon name={TREND_ICON[kpi.direction]} aria-hidden="true" />
      {/* The arrow follows the metric's polarity, not its sign: NDI falling is
          an up-arrow's worth of good news. The raw delta is shown alongside so
          the direction can always be checked against the number. */}
      {sign}
      {kpi.delta} vs. prior {days} days
    </span>
  );
}
